import React from "react"
import { RecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ModalityGraphGroupConfig } from "../../../../Types/ModalityGraphGroup"
import { getVisibleGraphs } from "../visibleGraphs"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { useMobergMenu } from "../../../../../../../../Hooks/useMobergMenu"
import { useState } from "react"
import { TraceDataConfig } from "../../../../Types/Trace"
import { MobergContextMenuItem } from "../../../../../../../../Components/MobergMenu/MobergContextMenuItem"
import { MdVisibility, MdVisibilityOff } from "react-icons/md"
import { MobergIconSize } from "../../../../../../../../Moberg"
import { MobergMenuDivider } from "../../../../../../../../Components/MobergMenu/MobergMenuDivider"
import { useEMRDataProvider } from "../../../../../../../../Providers/EMRDataProvider"
import { EMRDataStreamId } from "../../../../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications"


export const useTraceVisibilityContextMenu = (atom: RecoilState<ModalityGraphGroupConfig>) => {
    const { emrData } = useEMRDataProvider()
    const setConfig = useSetRecoilState(atom)
    const { patientModalities } = useRecoilValue(currentPatientFileInfoAtom)
    const [traceVisibilityMenuData, setTraceVisibilityMenuData] = useState<{graphId: string, traceConfig: TraceDataConfig}>()
    const menu = useMobergMenu({ placement: "right-start", isContextMenu: true })

    const setTraceVisibility = (getVisibility: (graphId: string, traceId: string, value: boolean) => boolean) => {
        setConfig(previous => {
            const newGraphs = previous.graphs.map(graph => ({
                ...graph,
                traces: graph.traces.map(trace => ({
                    ...trace, 
                    isVisible: getVisibility(graph.id, trace.id, trace.isVisible ?? true)
                }))
            }))

            // If there are no visible graphs, make sure "hideEmptyGraphs" is false, otherwise, no change
            const noVisibleGraphs = getVisibleGraphs(newGraphs, patientModalities, emrData, true).length === 0
            return {
                ...previous, 
                graphs: newGraphs, 
                hideEmptyGraphs: noVisibleGraphs ? false : previous.hideEmptyGraphs 
            }
        })
        menu.close()
    }

    const setEMRDataVisibility = (graphId: string, traceId: string, getVisibility: (graphId: string, traceId: string, value: boolean) => boolean) => {

        setConfig(previous => {
            const newEMRDataConfigs = [...(previous.EMRDataConfigs ?? [])]

            // Make sure new medications get properly added
            const found = newEMRDataConfigs.find(config => config.graphId === graphId && config.traceId === traceId)

            if (!found) {
                newEMRDataConfigs.push({ graphId, traceId, isVisible: true })
            }

            // Make sure all other EMR data have visibility configs too, in the case of "hide all".
			emrData.forEach(harmonizedEMRData => {
                harmonizedEMRData.dataStreams.forEach(dataStream => {
                    const emrDataId: EMRDataStreamId = { id: harmonizedEMRData.id, code: dataStream.code, units: dataStream.units }
                    const EMRDataConfig = { graphId, traceId: JSON.stringify(emrDataId), isVisible: true }
                    
                    if (!newEMRDataConfigs.find(entry => entry.graphId === EMRDataConfig.graphId && entry.traceId === EMRDataConfig.traceId)) {
                        newEMRDataConfigs.push(EMRDataConfig)
                    }
                })
			})

            return {
                ...previous,
                EMRDataConfigs: newEMRDataConfigs.map(entry => ({...entry, isVisible: getVisibility(entry.graphId, entry.traceId, entry.isVisible)}))
            }
        })
    }

    const toggleTraceVisibility = (graphId: string, traceId: string, isEMRData: boolean=false) => {
        const getVisibility = (_graphId: string, _traceId: string, isVisible: boolean) => graphId === _graphId && traceId === _traceId ? !isVisible : isVisible
        
        if (isEMRData) {
            setEMRDataVisibility(graphId, traceId, getVisibility)
        } else {
		    setTraceVisibility(getVisibility)
        }
	}

    const openTraceVisibilityContextMenu = (event: React.MouseEvent, traceConfig: TraceDataConfig, graphId: string) => {
        setTraceVisibilityMenuData({ graphId, traceConfig })
        menu.open(event)
    }

    const updateVisibility = (getVisibility: (graphId: string, traceId: string, value: boolean) => boolean) => {
        if (traceVisibilityMenuData?.traceConfig.isEMRData) {
            setEMRDataVisibility(traceVisibilityMenuData.graphId, traceVisibilityMenuData.traceConfig.id, getVisibility)
        } else {
            setTraceVisibility(getVisibility)
        }
    }

    const onlyShowSelectedTrace = () => {
        updateVisibility((graphId: string, traceId: string, currentVisibility: boolean) => (
            graphId === traceVisibilityMenuData?.graphId && traceId === traceVisibilityMenuData?.traceConfig.id 
                ? true 
                : graphId === traceVisibilityMenuData?.graphId 
                    ? false 
                    : currentVisibility
        ))
    }

    const showAllTraces = () => {
        updateVisibility((graphId: string, traceId: string, currentVisibility: boolean) => graphId === traceVisibilityMenuData?.graphId ? true : currentVisibility)
    }

    const hideAllTraces = () => {
        updateVisibility((graphId, traceId, currentVisibility) => graphId === traceVisibilityMenuData?.graphId ? false : currentVisibility)
    }

    const TraceVisibilityContextMenu = (
        <menu.MobergMenu>
            <MobergContextMenuItem onClick={onlyShowSelectedTrace}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Only show {traceVisibilityMenuData?.traceConfig?.name}
            </MobergContextMenuItem>

            <MobergMenuDivider />

            <MobergContextMenuItem onClick={showAllTraces}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Show all
            </MobergContextMenuItem>
            <MobergContextMenuItem onClick={hideAllTraces}
                icon={<MdVisibilityOff size={MobergIconSize.SMALL} />}>
                Hide all
            </MobergContextMenuItem>
        </menu.MobergMenu>
    )

    return { TraceVisibilityContextMenu, openTraceVisibilityContextMenu, setTraceVisibility, toggleTraceVisibility }
}